import { PageStyle } from '@models/Page';
import { SectionStyleSegment, SectionStyleType } from '@models/PageSection';

/**
 * Ref. [spec](https://dev.azure.com/Altibox/e872e564-da9e-4d6a-bef9-e2c54d22356a/_apis/wit/attachments/1c3cce35-8061-4d28-9b6e-1bf9504606ad?fileName=image.png)
 */
export const PageStyle_SectionStyleType: Record<PageStyle, SectionStyleType[]> = {
  swimlane: [
    'swimlane',
    'swimlane-with-background',
    'swimlane-with-background-and-left-text',
    'carousel',
    'live-programs',
    'user-channels-ongoing',
    'user-channels-catchup',
    'continue-watching',
    'my-recordings', // Web
    'bought-and-rented',
    'bought',
    'rented',
    'apps', // Model A
    'favorited-channels',
    'favorited-content'
  ],
  grid: [
    'sortable-grid',
    'plain-grid',
    'apps', // Model A
    'my-recordings', // Web
    'bought-and-rented',
    'bought',
    'rented',
    'favorited-channels',
    'favorited-content',
    'user-channels-ongoing',
    'user-channels-catchup',
  ],
};

/**
 * Ref. [spec](https://dev.azure.com/Altibox/e872e564-da9e-4d6a-bef9-e2c54d22356a/_apis/wit/attachments/1c3cce35-8061-4d28-9b6e-1bf9504606ad?fileName=image.png)
 */
export const SectionStyleType_SectionStylePattern: Record<SectionStyleType, SectionStyleSegment[]> =
  {
    swimlane: [
      'wide',
      'wide-airtime',
      'poster',
      'streaming-service',
      'promo-wide',
      'promo-wide-large',
      'genre',
      'person', // Model A
      'kids-service', // Model A
      'poster-notext', // Model A
      'wide-notext-large', // Model A
    ],
    'swimlane-with-background': [
      'wide',
      'poster',
      'streaming-service',
      'promo-wide',
      'promo-wide-large',
      'genre',
      'person', // Model A
      'kids-service', // Model A
      'poster-notext', // Model A
      'wide-notext-large', // Model A
    ],
    'swimlane-with-background-and-left-text': [
      'wide',
      'poster',
      'streaming-service',
      'promo-wide',
      'promo-wide-large',
      'genre',
      'person', // Model A
      'kids-service', // Model A
      'poster-notext', // Model A
      'wide-notext-large', // Model A
    ],

    carousel: [
      'banner-fullscreen',
      'wide-notext-large', // Q22
      'wide-notext-double', // Q22
    ],
    'live-programs': [
      'wide',
      'wide-notext-large', // Web
      'wide-notext-double', // Web
    ],
    'user-channels-ongoing': ['channel-ongoing'],
    'user-channels-catchup': ['channel'],
    'continue-watching': ['wide'],
    'bought-and-rented': ['poster'],
    bought: ['poster'],
    rented: ['poster'],
    'favorited-channels': ['wide', 'poster'],
    'favorited-content': ['wide', 'poster'],
    'my-recordings': ['wide'],
    apps: ['app'],
    'sortable-grid': ['wide', 'poster'],
    'plain-grid': ['wide', 'poster', 'streaming-service'],
    'streaming-services-footer': [],
  };

/**
 * Ref. [spec](https://dev.azure.com/Altibox/e872e564-da9e-4d6a-bef9-e2c54d22356a/_apis/wit/attachments/1c3cce35-8061-4d28-9b6e-1bf9504606ad?fileName=image.png)
 */
export const SectionStylePattern_SectionStylePattern: Record<
  SectionStyleSegment,
  SectionStyleSegment[]
> = {
  wide: ['wide'],
  'wide-airtime': ['wide-airtime'],
  poster: ['poster'],
  'channel-ongoing': ['channel-ongoing', 'channel'],
  channel: ['channel-ongoing', 'channel'],
  'streaming-service': ['streaming-service'],
  'banner-fullscreen': ['banner-fullscreen'],
  'promo-wide': ['promo-wide', 'promo-wide-large'],
  'promo-wide-large': ['promo-wide', 'promo-wide-large'],
  genre: ['genre'],
  app: ['app'],
  person: ['person'],
  'kids-service': ['kids-service'],
  'poster-notext': ['poster-notext', 'wide-notext-large', 'wide-notext-double'],
  'wide-notext-large': ['poster-notext', 'wide-notext-large', 'wide-notext-double'],
  'wide-notext-double': ['poster-notext', 'wide-notext-large', 'wide-notext-double'],
};

export const getValidSectionStyleTypes = (pageStyle: PageStyle) =>
  PageStyle_SectionStyleType[pageStyle];

export const getDefaultSectionStyleType = (pageStyle: PageStyle) =>
  getValidSectionStyleTypes(pageStyle)[0];

export const getValidSectionStylePatternValues = (
  styleType?: SectionStyleType,
  basePattern?: SectionStyleSegment
) => {
  if (!styleType) return [];

  const validPatternValuesForStyleType = SectionStyleType_SectionStylePattern[styleType];
  if (!basePattern) return validPatternValuesForStyleType;

  const validPatternValuesForBasePattern = SectionStylePattern_SectionStylePattern[basePattern];

  return (
    validPatternValuesForStyleType?.filter(patternValue =>
      validPatternValuesForBasePattern?.includes(patternValue)
    ) ?? []
  );
};

export const getDefaultStylePattern = (styleType?: SectionStyleType) =>
  getValidSectionStylePatternValues(styleType)[0];

export const isSectionStylePatternValid = (
  styleType: SectionStyleType,
  pattern: SectionStyleSegment[] | string
) => {
  const validPatternValues = getValidSectionStylePatternValues(styleType);

  return (Array.isArray(pattern) ? pattern : (pattern.split(',') as SectionStyleSegment[])).every(
    patternValue => validPatternValues.includes(patternValue)
  );
};
